import { useState, type FormEvent } from 'react';
import { apiErrorText, useRenumberPtoRevisionMutation, type PtoRenumberResult } from '@shared/api';
import { Button } from '@shared/ui/Button';
import { Modal } from '@shared/ui/Modal';
import { renumberSummary } from './logic';
import type { RdVolumeEditAccess } from './useRdVolumeEditAccess';
import { Buttons, CommentField, Done, ErrorText, Form, Muted, Warn } from './ui';

/**
 * «Перенумеровать» ревизию вручную (канон №1–2, выпуск 4): «Инженер+», с
 * подтверждением и причиной. Части строк — по основным надписям листов (карта
 * листов последнего «Обновить разделы»; только так меняется часть
 * пронумерованной строки), номера 1…N в каждой части РД в порядке документа —
 * даже закреплённые после волны; «было → стало», кто и причина — в журнал
 * правок. Количества, `record_uid` и привязки не меняются, но ссылки на строки
 * по номеру («СП01 №5») могут указать на другую строку — поэтому подтверждение.
 */
export function RenumberButton({
  revisionId,
  rights,
  disabled = false,
}: {
  revisionId: string;
  rights: RdVolumeEditAccess;
  /** Ревизия заменена или не проиндексирована. */
  disabled?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<PtoRenumberResult | null>(null);
  const [renumber, { isLoading }] = useRenumberPtoRevisionMutation();
  if (!rights.enabled || !rights.canEdit) return null;

  function close() {
    if (isLoading) return;
    setOpen(false);
    setReason('');
    setError(null);
    setResult(null);
  }

  async function submit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    if (!reason.trim()) return;
    try {
      const res = await renumber({ revisionId, reason: reason.trim() }).unwrap();
      setResult(res.renumbered);
    } catch (err) {
      setError(apiErrorText(err, 'Строки не перенумерованы.'));
    }
  }

  return (
    <>
      <Button
        $variant="secondary"
        $size="sm"
        disabled={disabled}
        title={
          disabled
            ? 'Перенумеровать можно действующую проиндексированную ревизию'
            : 'Номера строк заново: 1…N в каждой части РД в порядке документа'
        }
        onClick={() => setOpen(true)}
      >
        Перенумеровать…
      </Button>
      {open && (
        <Modal open title="Перенумеровать строки ревизии" onClose={close}>
          {result ? (
            <Form onSubmit={(e) => e.preventDefault()}>
              <Done role="status">{renumberSummary(result)}</Done>
              {result.changed.length > 0 && (
                <Muted>
                  {result.changed
                    .slice(0, 15)
                    .map(([from, to]) => `${from} → ${to ?? 'без номера'}`)
                    .join('; ')}
                  {result.changed_total > 15 ? ` … всего ${result.changed_total}` : ''}
                </Muted>
              )}
              <Muted>Пары «было → стало» — в журнале правок РД проекта.</Muted>
              <Buttons>
                <Button type="button" $variant="primary" onClick={close}>
                  Закрыть
                </Button>
              </Buttons>
            </Form>
          ) : (
            <Form onSubmit={(e) => void submit(e)}>
              <Warn>
                Номера строк станут 1…N в каждой части РД («СП01 №1…», «ВР01 №1…») в порядке
                документа — и вставки «N.k» получат сквозные номера. Часть строки — по основной
                надписи её листа (как в последнем «Обновить разделы»): строка, чей лист теперь
                относится к другой части, перейдёт в неё вместе со своими ручными строками.
                Количества, record_uid и привязки не меняются, но ссылки по номеру строки («СП01
                №5») могут указать на другую строку. «Было → стало», вы и причина запишутся в
                неудаляемый журнал правок.
              </Warn>
              <CommentField
                value={reason}
                onChange={setReason}
                id="rd-renumber-reason"
                label="Причина (обязательно)"
                placeholder="Например: после переобработки вставки «N.k» — сквозные номера по листу"
              />
              {error && <ErrorText>{error}</ErrorText>}
              <Buttons>
                <Button type="submit" $variant="primary" disabled={isLoading || !reason.trim()}>
                  {isLoading ? 'Перенумерация…' : 'Перенумеровать'}
                </Button>
                <Button type="button" $variant="secondary" onClick={close} disabled={isLoading}>
                  Отмена
                </Button>
              </Buttons>
            </Form>
          )}
        </Modal>
      )}
    </>
  );
}
